<template>
    <div class="add-episode-player-wrapper">
        <player-embed
            player-type="custom"
            :player-embed="playerEmbed"
            :show-hash="show.hash"
        />
    </div>
</template>
<script>
import { debounce } from 'lodash';
import { mapState } from 'vuex';
import PlayerEmbed from '@/components/Embed/PlayerEmbed';
import episodeModal from '@/utils/episodeModal';

export default {
    components: {
        PlayerEmbed,
    },

    props: {
        playerEmbed: {
            type: [Boolean, Object],
            default: null,
        },
        initializeEmbed: {
            required: true,
            type: Function,
        },
        setGlobalConfig: {
            required: true,
            type: Function,
        },
        setEmbedSetting: {
            required: true,
            type: Function,
        },
        show: {
            type: [Object, Boolean],
            default: () => ({}),
        },
    },

    data: () => ({
        customData: {},
    }),

    computed: {
        ...mapState({
            file: state => state.UIState.addEpisodeModal.file,
            episode: state => state.UIState.addEpisodeModal.episode,
        }),

        /**
         * Returns cover image URL.
         *
         * @returns {string} Cover image URL.
         */
        coverImageUrl() {
            if (! this.episode.artwork) {
                return this.show.cover.imageUrl;
            }

            if (this.episode.artwork instanceof File) {
                return URL.createObjectURL(this.episode.artwork);
            }

            return this.episode.artwork;
        },
    },

    watch: {
        'episode.title': debounce(function (title) {
            this.updateTrack({ title });
        }, 500),

        /**
         * Updates player audio file.
         *
         * @param  {File} file Audio file.
         */
        file(file) {
            if (! episodeModal.isAudioFileInitializing()) {
                return;
            }

            this.updateTrack({
                file: file instanceof File ? URL.createObjectURL(file) : '',
            });
        },

        /**
         * Updates player artwork.
         *
         * @param  {File|string} artwork Artwork.
         */
        'episode.artwork': function (artwork) {
            if (! this.playerEmbed) {
                return;
            }

            this.playerEmbed.setCover({
                ...this.show.cover,
                imageUrl: this.coverImageUrl,
            });
        },
    },

    /**
     * Initializes player.
     */
    created() {
        this.setGlobalConfig({
            buttonConfig: {},
            buttons: this.show.buttons,
            color: this.show.color,
            controls: this.show.controls,
            cover: {
                ...this.show.cover,
                imageUrl: this.coverImageUrl,
            },
            coverDisplay: this.show.coverDisplay,
            credits: false,
            position: 'bottom',
            sticky: false,
            style: 'regular',
            title: this.show.title,
            tracks: [this.getDefaultTrackData()],
        });

        this.initializeEmbed();
        window.addEventListener('loadFuseboxPlayer', () => {
            this.$nextTick(() => this.updateTrack());
        });
    },

    methods: {
        /**
         * Gets the default data of track for player.
         *
         * @returns {object} Default track data.
         */
        getDefaultTrackData() {
            return {
                description: this.episode.description || '',
                duration: this.episode.duration || 0,
                episodeNo: this.episode.episodeNo || 1,
                featured: this.episode.featured || false,
                file: this.episode.file || '',
                title: this.episode.title || ' ',
            };
        },

        /**
         * Updates track in player.
         *
         * @param  {object} newData New track data.
         */
        updateTrack(newData = {}) {
            if (! this.playerEmbed) {
                return;
            }

            this.customData = { ...this.customData, ...this.newData };
            this.playerEmbed.setTracks([{
                ...this.getDefaultTrackData(),
                ...this.customData,
            }]);
        },
    },
};
</script>
